/**
 * What a form host puts in its `<head>`.
 *
 * Two audiences, and they see different things:
 *
 *   - **A crawler** — WhatsApp, Instagram, Facebook — never runs this file.
 *     It is handed `build/forms.html`, a copy of the shell carrying the same
 *     card text as below with the practitioner's name left out, because a
 *     static file cannot know whose subdomain it was asked for. See
 *     `scripts/generate-og-variants.js` and the host rule in `deploy/nginx.conf`
 *     that picks it over the dashboard's own shell.
 *   - **A person** — who does run it, by which time the brand has been read,
 *     so everything below can carry the practitioner's name.
 *
 * Both read their words from `lib/site/formMeta.json` rather than writing them
 * out twice, so the card a crawler builds and the head a browser ends up with
 * cannot drift apart.
 */

import { useEffect } from "react";
import FORM_META from "../../lib/site/formMeta.json";

/**
 * The card text, which is the same for both forms on purpose.
 *
 * A link is shared long before anybody knows whether it will open the enquiry
 * form or a pre-consultation, and "Pre-consultation" over a preview is a word
 * for the practitioner's trade rather than for the person being asked to fill
 * it in.
 */
export const FORM_META_TITLE: string = FORM_META.title;

export const FORM_META_DESCRIPTION: string = FORM_META.description;

/**
 * Landscape, and served off whichever host the link was shared from.
 *
 * Width and height are named separately because the picture is 3:2 - one
 * number answered for both sides only while it was square. They are given at
 * all so a crawler knows the shape before the bytes arrive: it lays the card
 * out once, instead of reflowing when the picture lands.
 */
export const FORM_PREVIEW_IMAGE: string = FORM_META.image;
export const FORM_PREVIEW_WIDTH: string = FORM_META.imageWidth;
export const FORM_PREVIEW_HEIGHT: string = FORM_META.imageHeight;

/** The practitioner's name in front of the card title, where it is known. */
export const formMetaTitle = (brandName: string): string =>
  brandName ? `${brandName} | ${FORM_META_TITLE}` : FORM_META_TITLE;

const setMeta = (attr: "name" | "property", key: string, content: string) => {
  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

/**
 * Leaves the tab without an icon.
 *
 * The manifest goes with the icons: its name and its pictures are Naurist's,
 * and a form is not something anybody installs to a home screen.
 *
 * Removing the links is not enough on its own — a browser with no icon named
 * asks for `/favicon.ico` at the root, and the form hosts serve the same files
 * as the dashboard, so Naurist's own mark would come back. An empty `data:`
 * URL is what stops the request being made at all.
 */
const blankIcons = () => {
  document.head
    .querySelectorAll(
      'link[rel~="icon"], link[rel="apple-touch-icon"], link[rel="manifest"]'
    )
    .forEach((link) => link.remove());

  const blank = document.createElement("link");
  blank.rel = "icon";
  blank.href = "data:,";
  document.head.appendChild(blank);
};

/**
 * Puts the practitioner's name on the tab and their card in the head.
 *
 * Nothing is restored on unmount: a public form is the whole of the visit, and
 * there is no dashboard behind it to hand the head back to.
 *
 * The tab names the practitioner first and then which of their two forms this
 * is — "Priya Nutrition | Enquiry form". Never the "Naurist | …" every
 * signed-in screen shows: whoever is filling this in came to talk to the
 * practitioner, not to us.
 *
 * `formName` is the tab's alone. The social card stays the same on both forms —
 * a link is shared long before anybody knows which one it will open, and
 * "Pre-consultation" over a preview is a word for the practitioner's trade
 * rather than for the person being asked to fill it in.
 */
export const usePublicFormHead = (brandName: string, formName: string): void => {
  useEffect(() => {
    document.title = brandName ? `${brandName} | ${formName}` : formName;

    const title = formMetaTitle(brandName);
    const image = `${window.location.origin}${FORM_PREVIEW_IMAGE}`;

    setMeta("name", "description", FORM_META_DESCRIPTION);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", window.location.href);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", FORM_META_DESCRIPTION);
    setMeta("property", "og:image", image);
    setMeta("property", "og:image:width", FORM_PREVIEW_WIDTH);
    setMeta("property", "og:image:height", FORM_PREVIEW_HEIGHT);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", FORM_META_DESCRIPTION);
    setMeta("name", "twitter:image", image);

    blankIcons();
  }, [brandName, formName]);
};

export default usePublicFormHead;
